import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X } from 'lucide-react';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { RootState } from '../../store/rootReducer';
import {
  selectCreateWorkspaceLoading,
  selectCreateWorkspaceError,
  selectCreateWorkspaceSuccess,
} from '../../store/workspace/workspace.selector';
import {
  createWorkspaceRequest,
  clearWorkspaceState,
} from '../../store/workspace/workspace.action';
import { useToast } from '../../contexts/ToastContext';

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [workspaceName, setWorkspaceName] = useState('');

  // Redux state
  const createWorkspaceLoading = useSelector((state: RootState) =>
    selectCreateWorkspaceLoading(state)
  );
  const createWorkspaceError = useSelector((state: RootState) =>
    selectCreateWorkspaceError(state)
  );
  const createWorkspaceSuccess = useSelector((state: RootState) =>
    selectCreateWorkspaceSuccess(state)
  );

  // Handle workspace creation success
  useEffect(() => {
    if (createWorkspaceSuccess) {
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Workspace created successfully!',
      });
      setWorkspaceName('');
      onClose();
      // Clear the success state to prevent re-triggering
      dispatch(clearWorkspaceState());
    }
  }, [createWorkspaceSuccess, showToast, onClose, dispatch]);

  // Handle workspace creation error
  useEffect(() => {
    if (createWorkspaceError) {
      showToast({
        type: 'error',
        title: 'Workspace Creation Error',
        message: `Failed to create workspace: ${createWorkspaceError}`,
      });
      dispatch(clearWorkspaceState());
    }
  }, [createWorkspaceError, showToast, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!workspaceName.trim()) {
      return;
    }

    // Dispatch the create workspace action
    dispatch(createWorkspaceRequest({ name: workspaceName.trim() }));
  };

  const handleClose = () => {
    setWorkspaceName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Blur Background Overlay */}
      <div
        className="fixed inset-0 bg-foreground/20 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 border border-gray-200 animate-in slide-in-from-top-2 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 bg-primary rounded-t-xl text-primary-foreground">
          <div>
            <h2 className="text-lg font-semibold">Create New Workspace</h2>
            <p className="text-sm text-primary-foreground/80 mt-1">
              Set up a new workspace for your team
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-primary-foreground hover:text-primary-foreground/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <Input
              label="Workspace Name"
              placeholder="Enter workspace name"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              required
              autoFocus
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createWorkspaceLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!workspaceName.trim() || createWorkspaceLoading}
            >
              {createWorkspaceLoading ? 'Creating...' : 'Create Workspace'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkspaceModal;
